import {
  Badge,
  BodyText,
  Box,
  Flex,
  RadioGroup,
  RadioTile,
} from '@utilitywarehouse/hearth-react';
import dayjs from 'dayjs';

import {
  type BroadbandAppointment,
  BroadbandAppointmentSlot,
} from '@generated/gql/graphql';
import { DateFormat } from '@src/types/index';

import { type SelectedAppointment } from '../types';

export interface NextAvailableSlotsProps {
  disabled?: boolean;
  availableSlots: BroadbandAppointment[];
  selectedAppointment: SelectedAppointment;
  onSelectAppointment: (slot: SelectedAppointment) => void;
  currentAppointment?: BroadbandAppointment;
}

const slotLabel = (slot: BroadbandAppointmentSlot) =>
  slot === BroadbandAppointmentSlot.Am
    ? 'Morning (8am - 1pm)'
    : 'Afternoon (1pm - 6pm)';

export const NextAvailableSlots = ({
  disabled = false,
  availableSlots,
  onSelectAppointment,
  selectedAppointment,
  currentAppointment,
}: NextAvailableSlotsProps) => {
  const nextAvailableSlotsGroupedByMonth = availableSlots
    .slice(0, 4)
    .reduce<{ [key: string]: BroadbandAppointment[] }>((acc, slot) => {
      const month = dayjs(slot.date).format('MMMM YYYY');
      acc[month] = acc[month] || [];
      acc[month].push(slot);
      return acc;
    }, {});

  const handleSelectNextAvailableSlot = (selectedSlotValue: string) => {
    const { date, slot }: SelectedAppointment = JSON.parse(selectedSlotValue);
    onSelectAppointment({ date, slot, suggested: true });
  };

  return (
    <Box asChild width="100%">
      <RadioGroup
        label="Or select from our earliest available slots"
        disabled={disabled}
        onValueChange={handleSelectNextAvailableSlot}
        value={JSON.stringify({
          date: selectedAppointment.date,
          slot: selectedAppointment.slot,
        })}
      >
        {Object.keys(nextAvailableSlotsGroupedByMonth).map((month) => (
          <Flex direction="column" key={month} width="100%" spacing="md">
            <BodyText color="secondary" asChild>
              <h3 id={`${month}-label`}>{month}</h3>
            </BodyText>
            {nextAvailableSlotsGroupedByMonth[month].map(({ date, slot }) => {
              const value = JSON.stringify({ date, slot });
              const isCurrentAppointment =
                currentAppointment &&
                date === currentAppointment.date &&
                slot === currentAppointment.slot;

              return (
                <RadioTile
                  key={value}
                  badge={
                    isCurrentAppointment && (
                      <Badge variant="subtle" colorScheme="info" size="sm">
                        Current appointment
                      </Badge>
                    )
                  }
                  aria-label={`${month}. ${dayjs(date).format(DateFormat.DisplayMonthName)} - ${slotLabel(slot)}`}
                  label={dayjs(date).format('dddd, Do')}
                  helperText={slotLabel(slot)}
                  value={value}
                />
              );
            })}
          </Flex>
        ))}
      </RadioGroup>
    </Box>
  );
};
